import { IOccupancyStats, StringUtils } from '@localina/core';
import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { getSlotDuration, getSlotMinutes, ITimeRange, toPercent } from '../../RestaurantStatsTimeline';

// Above 1 means booked past capacity; such bars are drawn full height and flagged.
export const isOverCapacity = (occupancy: number): boolean => occupancy > 1;

const getBarState = (occupancy: number, average: number): string | false =>
    isOverCapacity(occupancy) ? 'over-capacity' : occupancy >= average && 'exceeded';

interface IProps {
    stats: IOccupancyStats;
    // The span the chart's width stands for; bars are placed by their slot time within it.
    range: ITimeRange;
    // Off for a chart whose average a chart to its left already labels.
    showAverageLabel?: boolean;
}

export const RestaurantStatsChart: React.FC<IProps> = (props) => {
    const { t } = useTranslation();
    const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);
    const [pinnedIndex, setPinnedIndex] = React.useState<number | null>(null);
    const [tooltipPosition, setTooltipPosition] = React.useState<{ top: number; left: number } | null>(null);
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const barRefs = React.useRef<Map<number, HTMLButtonElement>>(new Map());

    const activeIndex = hoveredIndex ?? pinnedIndex;

    React.useEffect(() => {
        if (pinnedIndex === null) {
            return;
        }
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setPinnedIndex(null);
            }
        };
        const onPointerDown = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setPinnedIndex(null);
            }
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onPointerDown);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('mousedown', onPointerDown);
        };
    }, [pinnedIndex]);

    React.useLayoutEffect(() => {
        if (activeIndex === null) {
            return;
        }
        const bar = barRefs.current.get(activeIndex);
        if (!bar) {
            return;
        }
        const rect = bar.getBoundingClientRect();
        setTooltipPosition({ top: rect.top, left: rect.left + rect.width / 2 });
    }, [activeIndex]);

    React.useEffect(() => {
        if (activeIndex === null) {
            return;
        }
        const onScroll = () => {
            const bar = barRefs.current.get(activeIndex);
            if (bar) {
                const rect = bar.getBoundingClientRect();
                setTooltipPosition({ top: rect.top, left: rect.left + rect.width / 2 });
            }
        };
        document.addEventListener('scroll', onScroll, true);
        return () => document.removeEventListener('scroll', onScroll, true);
    }, [activeIndex]);

    const slots = getSlotMinutes(props.stats.list, props.range.from);
    const slotWidth = (getSlotDuration(slots) / (props.range.to - props.range.from)) * 100;

    const active = activeIndex !== null ? props.stats.list[activeIndex] : undefined;
    const averageHeight = 100 * Math.min(props.stats.average, 1);

    return (
        <div className="restaurant-stats__chart" ref={containerRef}>
            <div className="chart" onMouseLeave={() => setHoveredIndex(null)}>
                {props.showAverageLabel !== false && (
                    <span className="chart__average-label" style={{ bottom: `${averageHeight}%` }}>
                        {Math.round(props.stats.average * 100)}%
                    </span>
                )}
                {props.stats.list.map((it, index) => (
                    <button
                        key={it.time}
                        type="button"
                        ref={(el) => {
                            if (el) {
                                barRefs.current.set(index, el);
                            } else {
                                barRefs.current.delete(index);
                            }
                        }}
                        className={StringUtils.combineStrings([
                            'chart__bar',
                            getBarState(it.occupancy, props.stats.average),
                            index === activeIndex && 'is-hovered',
                        ])}
                        style={{
                            left: `${toPercent(slots[index], props.range)}%`,
                            width: `calc(${slotWidth}% - 1px)`,
                            height: `${100 * Math.min(it.occupancy, 1)}%`,
                        }}
                        onMouseEnter={() => setHoveredIndex(index)}
                        onFocus={() => setHoveredIndex(index)}
                        onBlur={() => setHoveredIndex(null)}
                        onClick={(event) => {
                            event.stopPropagation();
                            setPinnedIndex((current) => (current === index ? null : index));
                        }}
                        aria-label={`${it.time}, ${Math.round(it.occupancy * 100)}%`}
                        aria-pressed={pinnedIndex === index}
                    />
                ))}
                <div className="chart__line" style={{ bottom: `${averageHeight}%` }} />
            </div>
            {active &&
                tooltipPosition &&
                createPortal(
                    <div
                        className={StringUtils.combineStrings([
                            'chart__tooltip',
                            getBarState(active.occupancy, props.stats.average),
                        ])}
                        style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
                        role="status"
                    >
                        <div className="chart__tooltip__time">{active.time}</div>
                        <div className="chart__tooltip__value">{Math.round(active.occupancy * 100)}%</div>
                        {isOverCapacity(active.occupancy) && (
                            <div className="chart__tooltip__status">{t('dashboard.chart.legend.overCapacity')}</div>
                        )}
                    </div>,
                    document.body,
                )}
        </div>
    );
};
